import React, { useEffect, useState } from "react";
import { FiPlus, FiEdit, FiCreditCard, FiTrash2 } from "react-icons/fi";
import Swal from "sweetalert2";
import { getAllBankDetails, deleteDirectorBankDetails } from "../../services/adminApi";
import "./PaymentSettlementCustom.css";

const PaymentMasterList = ({ onAdd, onEdit, onDelete }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadRecords = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getAllBankDetails();
      if (res.data?.success) {
        setRecords(res.data.data || []);
      } else {
        setRecords([]);
        setError(res.data?.message || "No bank accounts found.");
      }
    } catch (error) {
      setRecords([]);
      setError(error.response?.data?.message || "Failed to load bank accounts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  // ✏️ EDIT CLICK HANDLER (Guarantees exact User ID is passed)
  const handleEditClick = (record) => {
    const targetUserId = record.id || record.userId || record.user_id || record.castingDirectorId;

    // Normalize record so edit form gets exact ID in all properties
    const normalizedRecord = {
      ...record,
      id: targetUserId,
      userId: targetUserId,
      user_id: targetUserId,
      castingDirectorId: targetUserId,
      casting_director_id: targetUserId
    };

    if (onEdit) {
      onEdit(normalizedRecord);
    }
  };

  // 🗑️ DELETE WITH SWEETALERT2 CONFIRMATION
  const handleDeleteClick = (record) => {
    const userId = record.id || record.userId || record.user_id || record.castingDirectorId;
    const name = getDirectorName(record);

    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete bank details for "${name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          if (onDelete) {
            await onDelete(record);
            loadRecords(); // Refresh list after parent delete
          } else {
            const res = await deleteDirectorBankDetails(userId);
            if (res.data?.success) {
              Swal.fire("Deleted!", "Bank details deleted successfully.", "success");
              loadRecords(); // Refresh list
            } else {
              Swal.fire("Error!", res.data?.message || "Failed to delete bank details.", "error");
            }
          }
        } catch (err) {
          Swal.fire("Error!", err.response?.data?.message || "Something went wrong while deleting.", "error");
        }
      }
    });
  };

  const maskAccount = (accNo) => {
    if (!accNo) return "-";
    const val = String(accNo);
    return val.length <= 4 ? val : `****${val.slice(-4)}`;
  };

  const getDirectorName = (r) => r.name || r.full_name || r.castingDirectorName || r.casting_director_name || r.user?.full_name || "Unknown";
  const getDirectorEmail = (r) => r.email || r.user?.email || "";
  const getBankName = (r) => r.bankName || r.bank_name || "-";
  const getAccountNumber = (r) => r.accountNumber || r.account_number || "";
  const getIFSC = (r) => r.ifscCode || r.ifsc_code || "-";
  const getAccountType = (r) => r.accountType || r.account_type || "-";
  const getFrequency = (r) => r.frequency || r.settlementFrequency || r.settlement_frequency || "-";
  const getStatus = (r) => r.bankStatus || r.status || "Inactive";

  return (
    <div className="bma-settlement-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <div className="bma-breadcrumb">Home &nbsp;&gt;&nbsp; Casting Directors &nbsp;&gt;&nbsp; <strong>Payment Master</strong></div>
          <h1 className="bma-page-title">Payment Master 11</h1>
          <p className="bma-page-subtitle">Manage Casting Director bank accounts and settlement preferences.</p>
        </div>
        <button type="button" className="bma-btn-red" onClick={onAdd}><FiPlus /> Add Bank Account</button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="bma-card">
        <div className="bma-card-body">
          <div className="d-flex align-items-center gap-2 mb-3">
            <div className="bma-icon-circle"><FiCreditCard /></div>
            <h2 className="bma-card-title">Bank Account List</h2>
          </div>

          {loading && <div className="text-center py-5">Loading bank accounts...</div>}

          {!loading && records.length === 0 && (
            <div className="text-center py-5">
              <FiCreditCard size={45} style={{ opacity: 0.4 }} />
              <h5 className="mt-3">No Bank Account Added</h5>
              <p className="text-muted">No Payment Master record has been added yet.</p>
              <button type="button" className="bma-btn-red" onClick={onAdd}><FiPlus /> Add Bank Account</button>
            </div>
          )}

          {!loading && records.length > 0 && (
            <div className="table-responsive">
              <table className="table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Casting Director</th>
                    <th>Bank Name</th>
                    <th>Account Number</th>
                    <th>IFSC</th>
                    <th>Account Type</th>
                    <th>Frequency</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, index) => (
                    <tr key={r.id || r.bankDetailsId || index}>
                      <td>{index + 1}</td>
                      <td>
                        <div>
                          <strong>{getDirectorName(r)}</strong>
                          {getDirectorEmail(r) && <small className="d-block text-muted">{getDirectorEmail(r)}</small>}
                        </div>
                      </td>
                      <td>{getBankName(r)}</td>
                      <td>{maskAccount(getAccountNumber(r))}</td>
                      <td>{getIFSC(r)}</td>
                      <td>{getAccountType(r)}</td>
                      <td>{getFrequency(r)}</td>
                      <td>
                        <span className={getStatus(r) === "Active" ? "badge badge-success" : "badge badge-secondary"}>
                          {getStatus(r)}
                        </span>
                      </td>
                      <td>
                        <div className="bma-action-buttons">
                          <button type="button" className="bma-action-edit" title="Edit" onClick={() => handleEditClick(r)}>
                            <FiEdit size={16} />
                          </button>
                          <button type="button" className="bma-action-delete" title="Delete" onClick={() => handleDeleteClick(r)}>
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentMasterList;